import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { UploadModal } from '@/components/UploadModal';
import { VideoCard, VideoCardSkeleton } from '@/components/VideoCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { isValidVideoEvent, deduplicateVideos, extractTitle, extractTags } from '@/hooks/useVideoFeed';
import type { VideoEvent } from '@/hooks/useVideoFeed';
import { useAdminAnalytics, useAdminConfig, useSearchRelays, canView } from '@/hooks/useAdminConfig';
import type { NostrEvent, NostrMetadata } from '@nostrify/nostrify';
import { NSchema as n } from '@nostrify/nostrify';

// ── Video search ─────────────────────────────────────────────────────────────

function useVideoSearch(query: string) {
  const { nostr } = useNostr();
  const { config: adminConfig } = useAdminConfig();
  const searchRelays = useSearchRelays();
  return useQuery({
    queryKey: ['video-search', query, searchRelays],
    queryFn: async (ctx) => {
      if (!query) return [];
      const q = query.toLowerCase();

      // Use dedicated search relays when configured, otherwise fall back to pool
      const searchNostr = searchRelays.length > 0 ? nostr.group(searchRelays) : nostr;

      // Run relay full-text search and a broad recent fetch in parallel,
      // then merge + deduplicate + client-side filter by title/tags.
      const [searchResults, recentResults] = await Promise.all([
        searchNostr.query(
          [{ kinds: [21, 22, 34235, 34236], search: query, limit: 50 }],
          { signal: ctx.signal },
        ).catch(() => [] as NostrEvent[]),
        searchNostr.query(
          [{ kinds: [21, 22, 34235, 34236], limit: 200 }],
          { signal: ctx.signal },
        ).catch(() => [] as NostrEvent[]),
      ]);

      const combined = [...searchResults, ...recentResults];
      const valid = deduplicateVideos(combined.filter(isValidVideoEvent));

      // Client-side filter: title or tags must contain the query words
      const words = q.split(/\s+/).filter(Boolean);
      const matched = valid.filter(event => {
        const title = extractTitle(event).toLowerCase();
        const tags = extractTags(event).join(' ').toLowerCase();
        const content = event.content.toLowerCase();
        return words.every(w => title.includes(w) || tags.includes(w) || content.includes(w));
      });

      return matched.filter(e => canView(e.pubkey, adminConfig)) as VideoEvent[];
    },
    enabled: !!query,
    staleTime: 60000,
  });
}

// ── User search ──────────────────────────────────────────────────────────────

interface UserResult {
  event: NostrEvent;
  metadata: NostrMetadata;
}

function useUserSearch(query: string) {
  const { nostr } = useNostr();
  const searchRelays = useSearchRelays();
  return useQuery({
    queryKey: ['user-search', query, searchRelays],
    queryFn: async (ctx) => {
      if (!query) return [];
      const q = query.toLowerCase();

      // Use dedicated search relays when configured, otherwise fall back to pool
      const searchNostr = searchRelays.length > 0 ? nostr.group(searchRelays) : nostr;

      // NIP-50 full-text search on kind 0
      const events = await searchNostr.query(
        [{ kinds: [0], search: query, limit: 30 }],
        { signal: ctx.signal },
      ).catch(() => [] as NostrEvent[]);

      const results: UserResult[] = [];
      const seen = new Set<string>();

      for (const event of events) {
        if (seen.has(event.pubkey)) continue;
        try {
          const metadata = n.json().pipe(n.metadata()).parse(event.content);
          const name = (metadata.name ?? '').toLowerCase();
          const display = (metadata.display_name ?? '').toLowerCase();
          const nip05 = (metadata.nip05 ?? '').toLowerCase();
          const about = (metadata.about ?? '').toLowerCase();
          // Keep if any field contains the query
          const words = q.split(/\s+/).filter(Boolean);
          const matches = words.every(w =>
            name.includes(w) || display.includes(w) ||
            nip05.includes(w) || about.includes(w) ||
            event.pubkey.startsWith(w),
          );
          if (matches) {
            seen.add(event.pubkey);
            results.push({ event, metadata });
          }
        } catch {
          // skip unparseable kind 0
        }
      }

      return results;
    },
    enabled: !!query,
    staleTime: 60000,
  });
}

// ── User result card ─────────────────────────────────────────────────────────

function UserResultCard({ result }: { result: UserResult }) {
  const { metadata, event } = result;
  const displayName = metadata.name ?? metadata.display_name ?? event.pubkey.slice(0, 16) + '…';

  return (
    <Link
      to={`/profile/${event.pubkey}`}
      className="flex items-center gap-3 px-4 py-3 rounded-xl no-underline transition-colors"
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--gb-border)',
      }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--gb-accent)')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--gb-border)')}
    >
      <Avatar className="h-12 w-12 shrink-0">
        <AvatarImage src={metadata.picture} />
        <AvatarFallback
          className="text-sm font-bold"
          style={{ background: 'var(--gb-accent)', color: '#fff' }}
        >
          {displayName[0]?.toUpperCase() ?? '?'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
            {displayName}
          </span>
          {metadata.nip05 && (
            <span className="text-xs truncate" style={{ color: '#1da1f2' }}>
              ✓ {metadata.nip05}
            </span>
          )}
        </div>
        {metadata.about && (
          <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
            {metadata.about}
          </p>
        )}
      </div>
      <svg
        width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" className="shrink-0"
        style={{ color: 'var(--text-secondary)' }}
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    </Link>
  );
}

function UserResultSkeleton() {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl"
      style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)' }}
    >
      <Skeleton className="h-12 w-12 rounded-full shrink-0" style={{ background: 'var(--bg-primary)' }} />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" style={{ background: 'var(--bg-primary)' }} />
        <Skeleton className="h-3 w-48" style={{ background: 'var(--bg-primary)' }} />
      </div>
    </div>
  );
}

// ── Tab button ───────────────────────────────────────────────────────────────

function TabBtn({
  active, onClick, children, count,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors"
      style={{
        color: active ? 'var(--gb-accent)' : 'var(--text-secondary)',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--gb-accent)' : '2px solid transparent',
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {children}
      {count !== undefined && (
        <span
          className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
          style={{
            background: active ? 'var(--gb-accent)' : 'var(--bg-secondary)',
            color: active ? '#fff' : 'var(--text-secondary)',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

type Tab = 'videos' | 'people';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [uploadOpen, setUploadOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('videos');
  const { recordSearch } = useAdminAnalytics();

  const { data: videoResults, isLoading: videosLoading } = useVideoSearch(q);
  const { data: userResults, isLoading: usersLoading } = useUserSearch(q);

  // Switch to people tab if no videos but there are people results
  useEffect(() => {
    if (!videosLoading && !usersLoading) {
      if ((videoResults?.length ?? 0) === 0 && (userResults?.length ?? 0) > 0) {
        setActiveTab('people');
      }
    }
  }, [videosLoading, usersLoading, videoResults, userResults]);

  // Reset to videos tab when query changes
  useEffect(() => {
    setActiveTab('videos');
  }, [q]);

  useEffect(() => {
    if (q.trim()) recordSearch(q.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useSeoMeta({
    title: q ? `"${q}" – Goblinbox Search` : 'Search – Goblinbox',
    description: `Search results for "${q}" on Goblinbox.`,
  });

  const videoCount = videoResults?.length ?? 0;
  const userCount = userResults?.length ?? 0;

  return (
    <Layout onUploadClick={() => setUploadOpen(true)}>
      <div className="px-4 sm:px-6 py-6" style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {q ? `Results for "${q}"` : 'Search'}
          </h1>
        </div>

        {!q ? (
          /* Empty state — no query yet */
          <div className="rounded-xl py-16 px-8 text-center" style={{ border: '2px dashed var(--gb-border)' }}>
            <div className="text-5xl mb-4">🔍</div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Enter a search term above to find videos and people.
            </p>
          </div>
        ) : (
          <>
            {/* Tab bar */}
            <div
              className="flex mb-6"
              style={{ borderBottom: '1px solid var(--gb-border)' }}
            >
              <TabBtn
                active={activeTab === 'videos'}
                onClick={() => setActiveTab('videos')}
                count={videosLoading ? undefined : videoCount}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                </svg>
                Videos
              </TabBtn>
              <TabBtn
                active={activeTab === 'people'}
                onClick={() => setActiveTab('people')}
                count={usersLoading ? undefined : userCount}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
                People
              </TabBtn>
            </div>

            {/* ── Videos tab ── */}
            {activeTab === 'videos' && (
              <>
                {videosLoading ? (
                  <div className="video-grid">
                    {Array.from({ length: 8 }).map((_, i) => <VideoCardSkeleton key={i} />)}
                  </div>
                ) : videoCount > 0 ? (
                  <>
                    <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                      {videoCount} video{videoCount !== 1 ? 's' : ''} found
                    </p>
                    <div className="video-grid">
                      {videoResults!.map(event => <VideoCard key={event.id} event={event} />)}
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl py-16 px-8 text-center" style={{ border: '2px dashed var(--gb-border)' }}>
                    <div className="text-5xl mb-4">🎬</div>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      No videos matched "{q}".
                    </p>
                    {userCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('people')}
                        className="mt-3 text-sm font-medium hover:underline"
                        style={{ color: 'var(--gb-accent)', background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        See {userCount} matching {userCount === 1 ? 'person' : 'people'} →
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── People tab ── */}
            {activeTab === 'people' && (
              <>
                {usersLoading ? (
                  <div className="flex flex-col gap-3">
                    {Array.from({ length: 5 }).map((_, i) => <UserResultSkeleton key={i} />)}
                  </div>
                ) : userCount > 0 ? (
                  <>
                    <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                      {userCount} {userCount === 1 ? 'person' : 'people'} found
                    </p>
                    <div className="flex flex-col gap-3 max-w-2xl">
                      {userResults!.map(r => <UserResultCard key={r.event.pubkey} result={r} />)}
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl py-16 px-8 text-center" style={{ border: '2px dashed var(--gb-border)' }}>
                    <div className="text-5xl mb-4">👤</div>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      No people matched "{q}".
                    </p>
                    {videoCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('videos')}
                        className="mt-3 text-sm font-medium hover:underline"
                        style={{ color: 'var(--gb-accent)', background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        See {videoCount} matching {videoCount === 1 ? 'video' : 'videos'} →
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </Layout>
  );
}
